import React from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Paper, 
  Button, 
  Grid,
  Fade,
  alpha 
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import {
  FamilyRestroom as FamilyIcon,
  Search as SearchIcon,
  Favorite as MatchIcon,
  AccountTree as TreeIcon
} from '@mui/icons-material';

// Styled Components
const StyledPaper = styled(Paper)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.light, 0.1)} 100%)`,
  borderRadius: theme.spacing(3),
  padding: theme.spacing(4),
  position: 'relative',
  overflow: 'hidden',
  boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
}));

const FeatureCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  height: '100%',
  background: alpha(theme.palette.background.paper, 0.6),
  borderRadius: theme.spacing(2),
  transition: 'all 0.3s ease',
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 12px 24px rgba(0,0,0,0.1)',
    '& .MuiSvgIcon-root': {
      transform: 'scale(1.1)',
    }
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  }
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(2),
  '& .MuiSvgIcon-root': {
    fontSize: 48,
    color: theme.palette.primary.main,
    transition: 'all 0.3s ease',
  }
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  padding: theme.spacing(1.5, 4),
  fontSize: '1.1rem',
  textTransform: 'none',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
  }
}));

const Welcome = () => {
  const theme = useTheme();

  const features = [
    {
      icon: <SearchIcon />,
      title: 'Find People',
      description: 'Search and connect with people in your community',
      link: '/goldpetals/find-people'
    },
    {
      icon: <MatchIcon />,
      title: 'Find Partner',
      description: 'Discover compatible partners based on your preferences',
      link: '/goldpetals/find-partner'
    },
    {
      icon: <TreeIcon />,
      title: 'Family Tree',
      description: 'Explore and visualize your family connections',
      link: '/goldpetals/my-family-tree'
    },
    {
      icon: <FamilyIcon />,
      title: 'Add Family',
      description: 'Grow your family tree by adding new members',
      link: '/goldpetals/home'
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Fade in timeout={1000}>
        <StyledPaper>
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography 
              variant="h2" 
              gutterBottom
              sx={{ 
                fontWeight: 700,
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                color: 'transparent',
              }}
            >
              Welcome to Gold Petals
            </Typography>
            <Typography 
              variant="h5" 
              color="text.secondary"
              sx={{ mb: 4, maxWidth: '800px', mx: 'auto' }}
            >
              Connect with your community, discover your roots, and build meaningful relationships
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <FeatureCard>
                  <IconWrapper>
                    {feature.icon}
                  </IconWrapper>
                  <Typography 
                    variant="h5" 
                    gutterBottom 
                    align="center"
                    sx={{ fontWeight: 600 }}
                  >
                    {feature.title}
                  </Typography>
                  <Typography 
                    variant="body1" 
                    color="text.secondary" 
                    align="center"
                    sx={{ mb: 3 }}
                  >
                    {feature.description}
                  </Typography>
                  <Box sx={{ textAlign: 'center' }}>
                    <StyledButton
                      variant="contained"
                      color="primary"
                      href={feature.link}
                    >
                      Explore
                    </StyledButton>
                  </Box>
                </FeatureCard>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <Typography 
              variant="h4" 
              gutterBottom
              sx={{ 
                fontWeight: 600,
                color: theme.palette.text.primary
              }}
            >
              Ready to Get Started?
            </Typography>
            <Typography 
              variant="body1" 
              color="text.secondary"
              sx={{ mb: 4, maxWidth: '600px', mx: 'auto' }}
            >
              Join our community today and discover the power of connections
            </Typography>
            <StyledButton
              variant="contained"
              color="primary"
              size="large"
              href="/goldpetals/signup"
              sx={{ mr: 2 }}
            >
              Create Account
            </StyledButton>
            <StyledButton
              variant="outlined"
              color="primary"
              size="large"
              href="/goldpetals/login"
            >
              Sign In
            </StyledButton>
          </Box>
        </StyledPaper>
      </Fade>
    </Container>
  );
};

export default Welcome; 